import { useCallback, useEffect, useRef, useState } from 'react';

function serialize(value: unknown): string {
	try {
		return JSON.stringify(value);
	} catch {
		return '';
	}
}

/**
 * Compare l'état courant du formulaire à un instantané pris quand `ready` passe à true
 * (fin du chargement, y compris les valeurs remplies automatiquement).
 * `markClean` remet cet instantané après un enregistrement qui reste sur la page.
 */
export function useFormDirty<T>(value: T, ready: boolean) {
	const baselineRef = useRef<string | null>(null);
	const valueRef = useRef(value);
	valueRef.current = value;
	const [isDirty, setIsDirty] = useState(false);

	useEffect(() => {
		if (!ready) {
			baselineRef.current = null;
			setIsDirty(false);
			return;
		}

		const serialized = serialize(value);
		if (baselineRef.current === null) {
			baselineRef.current = serialized;
			setIsDirty(false);
			return;
		}

		setIsDirty(serialized !== baselineRef.current);
	}, [value, ready]);

	const markClean = useCallback((next?: T) => {
		baselineRef.current = serialize(next ?? valueRef.current);
		setIsDirty(false);
	}, []);

	return { isDirty: ready && isDirty, markClean };
}

let bypassNextHistoryChange = false;

/** Autorise la prochaine navigation (sauvegarde réussie) sans popup de sortie. */
export function allowNextFormLeave() {
	bypassNextHistoryChange = true;
}

export function consumeFormLeaveBypass() {
	const allowed = bypassNextHistoryChange;
	bypassNextHistoryChange = false;
	return allowed;
}

/** Après une sauvegarde : quitte, ouvre l'édition de l'entité créée, ou reste et réinitialise l'état modifié. */
export function finishFormSave(
	shouldLeave: boolean,
	actions: {
		onLeave: () => void;
		stayOnEdit?: () => void;
		markClean: () => void;
	}
) {
	window.dispatchEvent(
		new CustomEvent('app:toast', {
			detail: { message: 'Modifications enregistrées', type: 'success' },
		})
	);
	actions.markClean();
	if (shouldLeave) {
		allowNextFormLeave();
		actions.onLeave();
		return;
	}
	if (actions.stayOnEdit) {
		allowNextFormLeave();
		actions.stayOnEdit();
	}
}
